import { Link } from 'react-router-dom';
import StatisticsPage, { drilldownPath, personPath } from '../../components/statistics/StatisticsPage';
import StatTile from '../../components/statistics/StatTile';
import DataTable from '../../components/statistics/DataTable';
import { BarList, LineChart } from '../../components/statistics/charts';
import { useStatisticsData, useStatisticsFilters } from '../../components/statistics/useStatistics';
import { getGrowth } from '../../services/statisticsService';
import { formatCount, formatDays, formatShare } from '../../utils/period';

/** PRD §5.4 — are people joining, getting started, and coming back? */
const Growth = () => {
  const [filters, setFilters] = useStatisticsFilters();
  const { data, loading, error, reload } = useStatisticsData(getGrowth, filters);
  const totals = data?.signups?.totals || {};
  const signups = Object.values(totals).reduce((n: number, v: any) => n + (v || 0), 0) as number;
  const funnel: any[] = data?.funnel || [];
  const returning: any[] | null = data?.returning ?? null;

  return (
    <StatisticsPage
      title="Growth"
      subtitle="Students who signed up in the period: how they joined, how far they got, and whether they came back."
      filters={filters}
      onFiltersChange={setFilters}
      period={data?.period}
      loading={loading}
      error={error}
      onRetry={reload}
      showComparison={false}
    >
      <div className="stats-tiles">
        <StatTile
          label="Sign-ups"
          current={signups}
          format={formatCount}
          detail={`Email ${formatCount(totals.email)} · Google ${formatCount(totals.google)} · Apple ${formatCount(totals.apple)}`}
          note={totals.unknown ? `${formatCount(totals.unknown)} with no recorded method (signed up before it was recorded, or deleted since).` : undefined}
          to={drilldownPath('signups', filters)}
        />
        <StatTile
          label="Became active"
          current={funnel[3]?.shareOfSignups ?? null}
          format={formatShare}
          detail={`${formatCount(funnel[3]?.count)} reached a first action`}
          note="A seat request, a ride offered or a Ride Capture. Searches leave no record."
        />
        <StatTile
          label="Time to first action"
          current={data?.times?.medianDaysToFirstAction ?? null}
          format={formatDays}
          detail="Median, from sign-up"
        />
        <StatTile
          label="Time to verify"
          current={data?.times?.medianDaysToVerified ?? null}
          format={formatDays}
          detail={`Median, from ${formatCount(data?.times?.verifiedTimingSample)} timed`}
          note="Only verifications since the time was first recorded."
        />
        <StatTile
          label="Joined through an invitation"
          current={data?.referrals?.share ?? null}
          format={formatShare}
          detail={`${formatCount(data?.referrals?.referredSignups)} sign-ups`}
        />
      </div>

      <section className="stats-section">
        <h2>Sign-ups by method</h2>
        <LineChart
          data={data?.signups?.series || []}
          series={[
            { key: 'email', label: 'Email' },
            { key: 'google', label: 'Google' },
            { key: 'apple', label: 'Apple' },
            ...(totals.unknown ? [{ key: 'unknown', label: 'Not recorded' }] : []),
          ]}
        />
      </section>

      <div className="stats-columns">
        <section className="stats-section">
          <h2>Onboarding funnel</h2>
          <p className="stats-section-intro">Each step counts only members who also passed the steps before it.</p>
          <BarList
            max={funnel[0]?.count || 1}
            items={funnel.map((s) => ({
              label: s.step,
              sub: s.dropOff === null ? undefined : `${formatShare(s.dropOff)} lost at this step`,
              value: s.count,
              display: `${formatCount(s.count)} · ${formatShare(s.shareOfSignups)}`,
            }))}
          />
        </section>

        <section className="stats-section">
          <h2>Coming back</h2>
          <p className="stats-section-intro">
            Opened the app again at least 7 or 30 days after joining. Only members who joined at least that long ago
            are counted.
          </p>
          {returning ? (
            <BarList
              max={1}
              items={returning.map((r) => ({
                label: `After ${r.days} days`,
                sub: `${formatCount(r.returned)} of ${formatCount(r.eligible)}`,
                value: r.share || 0,
                display: formatShare(r.share),
              }))}
            />
          ) : (
            <p className="stats-empty">Not available: PostHog is not configured or could not be reached.</p>
          )}
          <p className="stats-note">From in-app analytics: leaves out web-only members and anyone who declined analytics.</p>
        </section>
      </div>

      <section className="stats-section">
        <h2>Who is inviting</h2>
        <DataTable
          columns={[
            {
              key: 'fullName',
              label: 'Member',
              render: (r: any) => <Link to={personPath(r.userId)}>{r.fullName || `#${r.userId}`}</Link>,
            },
            { key: 'signups', label: 'Sign-ups from their invitations', numeric: true },
          ]}
          rows={data?.referrals?.topInviters || []}
          exportName="zippel-top-inviters"
          rowKey={(r: any) => r.userId}
          empty="No invited sign-ups in this period."
        />
      </section>
    </StatisticsPage>
  );
};

export default Growth;
